import { useState } from 'react';

import { ApiRequestError } from '@/lib/apiClient';
import type { ReverseGeocodedAddress } from '@/types/institute';
import { institutesApi } from '../api/institutes.api';

export interface LocatedAddress {
  latitude: number;
  longitude: number;
  /** Null when the lookup failed — the coordinates are still worth keeping. */
  address: ReverseGeocodedAddress | null;
}

export interface LocateStatus {
  tone: 'info' | 'warning';
  text: string;
}

/** Past this, a reading names the wrong street — said, not silently used. */
const ROUGH_ACCURACY_M = 100;

/** Seven places is what the column holds — about a centimetre. */
function round(value: number): number {
  return Math.round(value * 1e7) / 1e7;
}

/**
 * "Where am I, and what is the address here?" — for the campus address form.
 *
 * The browser's own location (it asks the person first), then the address at
 * that point from our server. Nothing is sent anywhere until they press the
 * button, and only the coordinates leave the browser.
 */
export function useCurrentAddress(
  /** Fills the form; returns the names of fields the lookup could not fill. */
  onLocated: (result: LocatedAddress) => string[],
) {
  const [isLocating, setIsLocating] = useState(false);
  const [status, setStatus] = useState<LocateStatus | null>(null);

  function locate() {
    if (!('geolocation' in navigator)) {
      setStatus({ tone: 'warning', text: 'This browser cannot share its location. Type the address instead.' });
      return;
    }

    setIsLocating(true);
    setStatus(null);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = round(position.coords.latitude);
        const longitude = round(position.coords.longitude);
        const metres = Math.round(position.coords.accuracy);

        let address: ReverseGeocodedAddress | null = null;
        let lookupError: string | null = null;
        try {
          address = await institutesApi.reverseGeocode(latitude, longitude);
        } catch (caught) {
          lookupError =
            caught instanceof ApiRequestError
              ? caught.message
              : 'The address could not be looked up. Type it in instead.';
        }

        const missing = onLocated({ latitude, longitude, address });
        setIsLocating(false);

        // Said in the order somebody reads the form, so they can work down it.
        const stillToFill =
          missing.length > 0
            ? ` Type in the ${missing.join(', ')} — the map did not have ${missing.length === 1 ? 'it' : 'them'}.`
            : '';
        setStatus(
          lookupError
            ? { tone: 'warning', text: `Location saved, but ${lookupError.charAt(0).toLowerCase()}${lookupError.slice(1)}` }
            : metres > ROUGH_ACCURACY_M
              ? {
                  tone: 'warning',
                  // A desktop on a wired connection is often placed in another
                  // city entirely — say so before it is saved as the campus.
                  text: `Located only to within ${(metres / 1000).toLocaleString('en-IN', { maximumFractionDigits: 1 })} km — on a computer this is often the wrong town. Use a phone at the campus, or type the address.${stillToFill}`,
                }
              : {
                  tone: missing.length > 0 ? 'warning' : 'info',
                  text: `Address filled from your location (within ${metres} m). Check it before saving.${stillToFill}`,
                },
        );
      },
      (error) => {
        setIsLocating(false);
        setStatus({
          tone: 'warning',
          text:
            error.code === error.PERMISSION_DENIED
              ? 'Location access was blocked. Allow it from the address bar, or type the address.'
              : 'Your location could not be found. Type the address instead.',
        });
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  }

  return { locate, isLocating, status, clearStatus: () => setStatus(null) };
}
